/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { MyShowsFilterItem, MyShowsTypeV2, SelectedMyShowsItem } from 'client-code/myshows';

import { MyshowFiltersError } from '../../core/error';
import { ServicesClientImpl } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import { KeyManager, ScreenTaskControllerTypes } from '../../core/navigation';
import { PlaybackContentType } from '../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../core/playback/impls';
import type { AppDispatch, TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TMyShowsItemList } from '../../core/types';
import { MyShowItemType } from '../../core/types/TMyShowsItemList';
import { ListTypes, UniqueIdentifier } from '../../core/utils';
import { NdvrPlaybackType } from '../../core/videoplayer/enums';
import { IPlayable } from '../../core/videoplayer/interfaces';
import { WhisperTypes } from '../../views/Whisper/WhisperTypes';
import { setListEntryFocus, setListEntryPosition } from '../list/list.actions';
import { onKeyDownList, onKeyUpList } from '../list/list.reducers';
import { showPreviousScreen } from '../screens/screens.reducers';
import { startVideoPlayback } from '../videoplayer/videoplayer.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    resetMyShows,
    setMyShowsErrorAction,
    setMyShowsFilterAction,
    setMyShowsItemsAction,
    setMyShowsItemsLoadingAction,
    setMyShowsPageLoadingAction,
} from './myshows.actions';
import {
    RESET_MYSHOWS,
    SET_MY_SHOWS_CARET,
    SET_MY_SHOWS_FILTER,
    SET_MY_SHOWS_ITEM_LOADING,
    SET_MY_SHOWS_ITEMS,
    SET_MY_SHOWS_PAGE_LOADING,
    SET_MYSHOWS_ERROR,
} from './myshows.types';
import { MyShowsListGenerator } from './MyShowsListGenerator';

const MAX_ITEM_IN_PAGE = 13;

interface IMyShowsState {
    myShowsItems: TMyShowsItemList;
    myShowsFilter: MyShowsFilterItem[];
    page: 0;
    isCaretVisible: boolean;
    isItemsLoading: boolean;
    isPageLoading: boolean;
    hasError: boolean;
    errorCode: string;
}

const initialState: IMyShowsState = {
    myShowsItems: {
        lastSelectedItem: {
            index: 0,
            myShowsItemId: '',
        },
        myShows: [],
        sortOrder: '',
        totalMyShowsCount: 0,
    },
    myShowsFilter: [],
    page: 0,
    isCaretVisible: false,
    isItemsLoading: true,
    isPageLoading: false,
    hasError: false,
    errorCode: '',
};

const TAG: string = 'MyShowsReducer';

const myShowsReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IMyShowsState>>{},
) => {
    switch (type) {
        case SET_MY_SHOWS_ITEMS:
            return {
                ...state,
                myShowsItems: payload.myShowsItems,
                page: payload.page,
            };
        case SET_MY_SHOWS_FILTER:
            return {
                ...state,
                myShowsFilter: payload.myShowsFilter,
            };
        case SET_MY_SHOWS_CARET:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case SET_MY_SHOWS_ITEM_LOADING:
            return {
                ...state,
                isItemsLoading: payload.isItemsLoading,
            };
        case SET_MY_SHOWS_PAGE_LOADING:
            return {
                ...state,
                isPageLoading: payload.isPageLoading,
            };
        case SET_MYSHOWS_ERROR:
            return {
                ...state,
                hasError: payload.hasError,
                errorCode: payload.errorCode,
            };
        case RESET_MYSHOWS:
            return initialState;
        default:
            return state;
    }
};

export const getItemsListHiglightPosition = (direction: 'up' | 'down', totalItems: number) => {
    let highLightIndex = direction === 'up' ? MAX_ITEM_IN_PAGE - 1 : 0;

    // relatively shift the focus for partialList
    if (MyShowsListGenerator.isListPartial())
        if (MyShowsListGenerator.isLastPage())
            highLightIndex = MAX_ITEM_IN_PAGE - (totalItems % MAX_ITEM_IN_PAGE);
        else if (MyShowsListGenerator.isFirstPage() && MyShowsListGenerator.isLastPageVisited())
            highLightIndex = (totalItems % MAX_ITEM_IN_PAGE) - 1;

    return highLightIndex;
};

export const getItemListLastSelectedIndex = (
    lastSelectedItem: SelectedMyShowsItem | undefined,
    totalItems: number,
) => {
    let currentItemIndex =
        (lastSelectedItem?.index === -1 ? 0 : lastSelectedItem?.index || 0) % MAX_ITEM_IN_PAGE;
    if (MyShowsListGenerator.isLastPage() && MyShowsListGenerator.isListPartial()) {
        // relatively shift the focus
        const noOfPreviousPageItems = MAX_ITEM_IN_PAGE - (totalItems % MAX_ITEM_IN_PAGE);
        currentItemIndex += noOfPreviousPageItems;
    }
    return currentItemIndex;
};

export const getMyShowsItems =
    (isFilterChanged?: boolean) => async (dispatch: Function, getState: Function) => {
        const {
            list: { [ListTypes.MYSHOWS_FILTER]: filterList },
        } = getState();
        const { myShowsFilter } = getState().myShows;
        const { position: myShowFilterPosition } = filterList;

        const { list, currentPage, networkCallId, hasError, errorCode } =
            await new MyShowsListGenerator().getList(
                UniqueIdentifier.getNextMyShowsNetworkCallId(),
                myShowsFilter[myShowFilterPosition].value,
            );

        if (networkCallId !== UniqueIdentifier.getMyShowsNetworkCallId()) return;
        if (hasError) {
            const whisperMessage = Locale.getString(LocaleKeys.MY_SHOWS_ERROR_MESSAGE).replace(
                '$ERROR_CODE',
                errorCode || '',
            );
            dispatch(setMyShowsErrorAction(true, errorCode || ''));
            dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
        }

        dispatch(setMyShowsItemsAction(list, currentPage));

        if (isFilterChanged) {
            // set last selectedIndex of the filter
            dispatch(
                setListEntryPosition(
                    ListTypes.MYSHOWS_ITEMS,
                    getItemListLastSelectedIndex(list.lastSelectedItem, list.totalMyShowsCount),
                ),
            );

            // clearing isItemsLoading here as this will remain at last in fast zap of filter
            dispatch(setMyShowsItemsLoadingAction(false));
        }
    };

export const setMyShowsPage = (page: number) => async (dispatch: AppDispatch) => {
    dispatch(setMyShowsPageLoadingAction(true));
    MyShowsListGenerator.setCurrentPage(page);
    await dispatch(getMyShowsItems());
    dispatch(setMyShowsPageLoadingAction(false));
};

export const highlightMyShowsItemList = () => (dispatch: AppDispatch, getState: TGetState) => {
    const { hasError } = getState().myShows;
    if (!hasError) dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
};

export const setLastSelectedItem = () => (dispatch: Function, getState: Function) => {
    const services = new ServicesClientImpl();
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
        myShows: { myShowsFilter, myShowsItems },
    } = getState();
    const currentFilter = myShowsFilter[filterList.position]?.value;
    const selectedItem = myShowsItems.myShows[itemList.position];
    MyShowsListGenerator.setLastSelectedItemAbsoluteIndex(itemList.position);
    if (!selectedItem) {
        return;
    }
    const { myShowsItemId } = selectedItem;
    if (myShowsItemId && currentFilter) {
        services.setLastSelectedItem(myShowsItemId, currentFilter).catch((_error) => {
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage);
        });
    }
};

export const setInitialValuesForMyShows = () => async (dispatch: Function) => {
    const services = new ServicesClientImpl();
    const myshowService = new MyShowsListGenerator();

    try {
        const res = await services.getMyShowsFilterList();
        const { currentFilterIndex, myShowsFilters } = res;
        dispatch(setMyShowsFilterAction(myShowsFilters, currentFilterIndex || 0));
        const { list, currentPage, networkCallId, hasError, errorCode } =
            await myshowService.getList(
                UniqueIdentifier.getNextMyShowsNetworkCallId(),
                myShowsFilters[currentFilterIndex || 0].value,
            );

        if (networkCallId !== UniqueIdentifier.getMyShowsNetworkCallId()) return;

        dispatch(setMyShowsItemsAction(list, currentPage));
        dispatch(setListEntryPosition(ListTypes.MYSHOWS_FILTER, currentFilterIndex || 0));
        dispatch(
            setListEntryPosition(
                ListTypes.MYSHOWS_ITEMS,
                getItemListLastSelectedIndex(list.lastSelectedItem, list.totalMyShowsCount),
            ),
        );

        // Shift focus to itemslist
        if (list.myShows.length > 0) {
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_FILTER, false));
        }

        if (hasError) {
            const whisperMessage = Locale.getString(LocaleKeys.MY_SHOWS_ERROR_MESSAGE).replace(
                '$ERROR_CODE',
                errorCode || '',
            );
            dispatch(setMyShowsErrorAction(true, errorCode || ''));
            dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
        }

        dispatch(setMyShowsItemsLoadingAction(false));
    } catch (_error: any) {
        const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
        const myshowError = new MyshowFiltersError(_error?.code, _error?.message);
        let whisperMessage = Locale.getString(LocaleKeys.MY_SHOWS_ERROR_MESSAGE).replace(
            '$ERROR_CODE',
            myshowError.getCode(),
        );
        const { list, currentPage } = await myshowService.getList(
            UniqueIdentifier.getNextMyShowsNetworkCallId(),
            undefined,
        );

        dispatch(setListEntryPosition(ListTypes.MYSHOWS_FILTER, 0));
        dispatch(setMyShowsItemsAction(list, currentPage));

        if (list && list.myShows.length > 0) {
            whisperMessage = Locale.getString(LocaleKeys.MY_SHOWS_FILTER_ERROR_MESSAGE).replace(
                '$ERROR_CODE',
                myshowError.getCode(),
            );
        }

        dispatch(
            setListEntryPosition(
                ListTypes.MYSHOWS_ITEMS,
                getItemListLastSelectedIndex(list.lastSelectedItem, list.totalMyShowsCount),
            ),
        );

        // Shift focus to itemslist
        if (list.myShows.length > 0) {
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_FILTER, false));
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
        }

        dispatch(setMyShowsItemsLoadingAction(false));
        dispatch(setMyShowsErrorAction(true, myshowError.getCode()));
        dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
        Log.e(TAG, errorMessage);
        Diagnostics.error(TAG, errorMessage);
    }
};

export const onKeyUpMyShowsOperations = () => async (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
        myShows: { page, myShowsItems, isPageLoading, hasError },
    } = getState();

    if (filterList.isFocused && filterList.position > 0) {
        if (hasError) dispatch(setMyShowsErrorAction(false, ''));
        dispatch(setMyShowsItemsLoadingAction(true));
        await dispatch(setLastSelectedItem());
        dispatch(onKeyUpList(ListTypes.MYSHOWS_FILTER));
        dispatch(setMyShowsItemsAction(initialState.myShowsItems, 0));
        MyShowsListGenerator.reset();
        dispatch(getMyShowsItems(true));
    } else if (itemList.isFocused && !isPageLoading) {
        if (itemList.position === 0 && page > 0) {
            dispatch(setMyShowsPageLoadingAction(true));
            MyShowsListGenerator.setCurrentPage(page - 1);
            const highLightIndex = getItemsListHiglightPosition(
                'up',
                myShowsItems.totalMyShowsCount,
            );

            await dispatch(getMyShowsItems());
            dispatch(setListEntryPosition(ListTypes.MYSHOWS_ITEMS, highLightIndex));
            dispatch(setMyShowsPageLoadingAction(false));
        } else dispatch(onKeyUpList(ListTypes.MYSHOWS_ITEMS));
    }
};

export const onKeyDownMyShowsOperation = () => async (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
        myShows: { page, myShowsItems, isPageLoading, hasError },
    } = getState();

    if (filterList.isFocused && filterList.position < filterList.limit - 1) {
        if (hasError) dispatch(setMyShowsErrorAction(false, ''));
        dispatch(setMyShowsItemsLoadingAction(true));
        await dispatch(setLastSelectedItem());
        dispatch(onKeyDownList(ListTypes.MYSHOWS_FILTER));
        dispatch(setMyShowsItemsAction(initialState.myShowsItems, 0));
        MyShowsListGenerator.reset();
        dispatch(getMyShowsItems(true));
    } else if (itemList.isFocused && !isPageLoading) {
        if (
            itemList.position === MAX_ITEM_IN_PAGE - 1 &&
            myShowsItems.totalMyShowsCount > (page + 1) * MAX_ITEM_IN_PAGE
        ) {
            dispatch(setMyShowsPageLoadingAction(true));
            MyShowsListGenerator.setCurrentPage(page + 1);
            const highLightIndex = getItemsListHiglightPosition(
                'down',
                myShowsItems.totalMyShowsCount,
            );

            await dispatch(getMyShowsItems());
            dispatch(setListEntryPosition(ListTypes.MYSHOWS_ITEMS, highLightIndex));
            dispatch(setMyShowsPageLoadingAction(false));
        } else dispatch(onKeyDownList(ListTypes.MYSHOWS_ITEMS));
    }
};

export const onKeyLeft = () => (dispatch: Function) => {
    dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, false));
    dispatch(setListEntryFocus(ListTypes.MYSHOWS_FILTER, true));
};

export const onKeyOkMyShowsFilter = () => (dispatch: Function) => {
    dispatch(setListEntryFocus(ListTypes.MYSHOWS_FILTER, false));
    dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
};

export const onKeyRight = () => (dispatch: Function) => {
    dispatch(setListEntryFocus(ListTypes.MYSHOWS_FILTER, false));
    dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
};

export const onKeyBackMyShowsOperations = () => async (dispatch: Function) => {
    await dispatch(setLastSelectedItem());
    MyShowsListGenerator.reset();
    dispatch(resetMyShows());
    dispatch(showPreviousScreen());
};

export const onCursorStateChangeMyShowsOperations =
    () => (dispatch: Function, getState: Function) => {
        const {
            list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
            screens: { screenTaskController },
        } = getState();

        if (
            screenTaskController === ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS &&
            !filterList.isFocused &&
            !itemList.isFocused
        ) {
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_FILTER, true));
        }
    };

export const triggerPlayBackForMyShowsItem =
    (currentItem: MyShowItemType) => (dispatch: Function) => {
        let id: string;
        let type: NdvrPlaybackType;
        if (currentItem.myShowsType === MyShowsTypeV2.MyShowsRecordingV2) {
            id = currentItem.recordingId as string;
            type = NdvrPlaybackType.RECORDING;
        } else if (currentItem.contentId) {
            id = currentItem.contentId;
            type = NdvrPlaybackType.CONTENT;
        } else {
            id = currentItem.unifiedCollectionId as string;
            type = NdvrPlaybackType.UNIFIED_COLLECTION;
        }

        if (!KeyManager.getInstance().isKeyIgnoring()) KeyManager.getInstance().startKeyIgnoring();

        const playbackModelImpl = PlaybackDataModelImpl.getInstance();
        playbackModelImpl.setPlaybackData(PlaybackContentType.NDVR_RECORDED);
        const playable: IPlayable = {
            // send record from my shows as we dont get whether isRecording content is played.
            // player will handle is played content has recording in progress or not.
            playbackContentType: PlaybackContentType.NDVR_RECORDED,
            ndvrPlaybackModel: {
                id,
                ndvrType: type,
            },
        };
        dispatch(startVideoPlayback(playable));
    };

export default myShowsReducer;
